<template>
  <Modal :open="open" @closed="$emit('closed')" dialog-size="modal-lg">
    <div class="modal-header bg-light">
      <h5 class="modal-title m-0" v-if="mode === 'add'">
        <i class="bi bi-plus-circle me-2"></i>Add Payment
      </h5>
      <h5 class="modal-title m-0" v-else>
        <i class="bi bi-pencil-square me-2"></i>Edit Payment
      </h5>
      <button @click="$emit('closed')" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
      </button>
    </div>
    <div class="modal-body">
      <form @submit.prevent="">
        <div class="row g-3">
          <div class="col-md-6">
            <label for="code" class="form-label fw-bold">Payment Code:</label>
            <div class="input-group">
              <span class="input-group-text"><i class="bi bi-upc"></i></span>
              <input type="text" class="form-control" v-model="localPayment.code" readonly>
            </div>
            <small class="form-text text-muted">Auto-generated code</small>
          </div>
          <div class="col-md-6">
            <label for="amount" class="form-label fw-bold">Amount:</label>
            <div class="input-group">
              <span class="input-group-text"><i class="bi bi-cash"></i></span>
              <input type="number" class="form-control" v-model="localPayment.amount" min="0" step="0.01" required>
            </div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-md-6">
            <label for="payment_method" class="form-label fw-bold">Payment Method:</label>
            <div class="input-group">
              <span class="input-group-text"><i class="bi bi-credit-card"></i></span>
              <select class="form-select" v-model="localPayment.payment_method" required>
                <option value="" disabled selected>Select payment method</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Mpesa">Mpesa</option>
                <option value="Cheque">Cheque</option>
                <option value="Credit">Credit</option>
                <option value="Cash">Cash</option>
              </select>
            </div>
          </div>
          <div class="col-md-6" v-if="!hideSupplierId">
            <label for="supplier_id" class="form-label fw-bold">Supplier:</label>
            <div class="input-group">
              <span class="input-group-text"><i class="bi bi-truck"></i></span>
              <select class="form-select" v-model="localPayment.supplier_id" :required="!hideSupplierId">
                <option value="" disabled selected>Select supplier</option>
                <option v-for="supplier in suppliers" :key="supplier.id" :value="supplier.id">
                  {{ supplier.name }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-md-6" v-if="!hidePurchaseId">
            <label for="purchase_id" class="form-label fw-bold">Purchase:</label>
            <div class="input-group">
              <span class="input-group-text"><i class="bi bi-cart"></i></span>
              <select class="form-select" v-model="localPayment.purchase_id" :required="preloadPurchaseId">
                <option value="">No specific purchase</option>
                <option v-for="purchase in purchases" :key="purchase.id" :value="purchase.id">
                  {{ purchase.code }}
                </option>
              </select>
            </div>
          </div>
          <div class="col-md-6">
            <label for="due_date" class="form-label fw-bold">Due Date:</label>
            <div class="input-group">
              <span class="input-group-text"><i class="bi bi-calendar"></i></span>
              <input type="date" class="form-control" v-model="localPayment.due_date" required>
            </div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-md-6">
            <label for="reference" class="form-label fw-bold">Reference (Optional):</label>
            <div class="input-group">
              <span class="input-group-text"><i class="bi bi-hash"></i></span>
              <input type="text" class="form-control" v-model="localPayment.reference" placeholder="Transaction reference">
            </div>
          </div>
        </div>

        <div class="row mt-3">
          <div class="col-12">
            <label for="notes" class="form-label fw-bold">Notes (Optional):</label>
            <div class="input-group">
              <span class="input-group-text"><i class="bi bi-card-text"></i></span>
              <textarea class="form-control" v-model="localPayment.notes" rows="3" placeholder="Additional notes"></textarea>
            </div>
          </div>
        </div>

        <div class="row mt-4">
          <div class="col-12">
            <div class="alert alert-info d-flex align-items-center">
              <i class="bi bi-info-circle-fill me-2"></i>
              <div>
                <strong>Note:</strong> Payment will be created with 'pending' status. Approve it when payment is completed.
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
    <div class="modal-footer bg-light">
      <button type="button" class="btn btn-outline-secondary" @click="$emit('closed')">
        <i class="bi bi-x-circle me-1"></i> Cancel
      </button>
      <button type="button" class="btn btn-primary" v-if="mode === 'add'" @click="handleSubmit">
        <i class="bi bi-plus-circle me-1"></i> Create Payment
      </button>
      <button type="button" class="btn btn-primary" v-else @click="handleSubmit">
        <i class="bi bi-check-circle me-1"></i> Update Payment
      </button>
    </div>
  </Modal>
</template>

<script>
import supplierPaymentService from "@/api/supplierPaymentService";
import {decrypt} from "@/utils/crypto";

export default {
  name: "AddPaymentModal",
  props: {
    open: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'add',
      validator: (value) => ['add', 'edit'].includes(value)
    },
    payment: {
      type: Object,
      default: () => ({
        organization_id: '',
        purchase_id: '',
        supplier_id: '',
        user_id: '',
        code: '',
        amount: '',
        payment_method: '',
        reference: '',
        status: 'pending',
        due_date: '',
        payment_date: null,
        notes: ''
      })
    },
    purchases: {
      type: Array,
      default: () => []
    },
    suppliers: {
      type: Array,
      default: () => []
    },
    preloadSupplierId: {
      type: [String, Number],
      default: null
    },
    preloadPurchaseId: {
      type: [String, Number],
      default: null
    },
    hideSupplierId: {
      type: Boolean,
      default: false
    },
    hidePurchaseId: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localPayment: { ...this.payment }
    }
  },
  watch: {
    async open(val) {
      if (val) {
        if (this.mode === 'add') {
          await this.initializePayment();
        } else {
          this.localPayment = { ...this.payment };
        }
      } else {
        this.resetPayment();
      }
    },
    payment: {
      handler(val) {
        this.localPayment = { ...val };
      },
      deep: true
    }
  },
  methods: {
    async initializePayment() {
      const nextNo = await supplierPaymentService.getSupplierPaymentNextNo();
      const profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
      const organization = JSON.parse(decrypt(sessionStorage.getItem('organization')));
      
      this.localPayment = {
        organization_id: organization.id,
        purchase_id: this.preloadPurchaseId || '',
        supplier_id: this.preloadSupplierId || '',
        user_id: profile.id,
        code: nextNo.next,
        amount: '',
        payment_method: '',
        reference: '',
        status: 'pending',
        due_date: '',
        payment_date: null,
        notes: ''
      };
    },
    resetPayment() {
      this.localPayment = {
        organization_id: '',
        purchase_id: '',
        supplier_id: '',
        user_id: '',
        code: '',
        amount: '',
        payment_method: '',
        reference: '',
        status: 'pending',
        due_date: '',
        payment_date: null,
        notes: ''
      };
    },
    handleSubmit() {
      this.$emit('submit', this.localPayment);
    }
  }
}
</script>

<style scoped>
</style>
